import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { navigationService } from 'vue-spatialnavigation';
import { RailResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { useCMSItemNavigation } from '@/composables/useCMSItemNavigation';
import { focusElementById } from '@/helpers/focus';
import api from '@/api';
import LuckyDipCell from '@/components/rails/LuckyDipCell.vue';
import GlyphView  from '@/components/glyphs/GlyphView.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'LuckyDipRail',
  props: {
    rail: null,
    railIndex: null,
    railDidNavigateDown: null
  },
  setup(__props: any) {

const props = __props as {
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
};




const { containerId, itemId, handleUp, handleDown, handleLeftFallback } =
  useRailNavigation(props);
const { openItem } = useCMSItemNavigation();

const instance = getCurrentInstance();
const lazyLoad = instance?.proxy?.$lazyLoad;

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const localised = (en: string | null, ga: string | null) => {
  const value = currentLanguage.value === Language.Irish ? ga : en;
  return value ?? en ?? '';
};

const title = computed(() => localised(props.rail.titleEn, props.rail.titleGa));
const subtitle = computed(() => localised(props.rail.subtitleEn, props.rail.subtitleGa));

const items = computed(() => props.rail.luckyDipItems ?? []);

const railStyle = computed(() => ({
  'background-color': props.rail.backgroundColor ?? undefined,
  color: props.rail.textColor ?? undefined,
}));


const loadingIndex = ref<number | null>(null);

const pickToken = ref(0);

const cancelPick = () => {
  pickToken.value += 1;
  loadingIndex.value = null;
};

const pickLuckyDip = async (index: number) => {
  if (loadingIndex.value !== null) return;

  const token = pickToken.value;
  loadingIndex.value = index;
  try {
    const item = await api.CMS.getLuckyDip();
    // Still the user's pending press, and this rail is still on screen.
    if (token === pickToken.value) openItem(item);
  } catch (error) {
    // Nothing to show the user on a remote: the tile simply comes back.
    console.warn('[rails] Lucky dip pick failed', error);
  } finally {
    if (token === pickToken.value) loadingIndex.value = null;
  }
};

const leaveUp = () => {
  cancelPick();
  handleUp();
};

const leaveDown = () => {
  cancelPick();
  handleDown();
};

onBeforeUnmount(cancelPick);

const focusItem = (index: number) => {
  const targetId = itemId(index);
  if (!navigationService.getFocusElementById(targetId)) return;

  const container = document.getElementById(containerId.value);
  const target = document.getElementById(targetId);
  if (container && target) {
    container.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  //Call helper
  focusElementById(targetId);
};

const moveLeft = (index: number) => {
  if (index === 0) {
    cancelPick();
    handleLeftFallback();
    return;
  }
  focusItem(index - 1);
};

const moveRight = (index: number) => {
  if (index < items.value.length - 1) focusItem(index + 1);
};

const scanForLazyImages = () => {
  setTimeout(() => {
    const container = document.getElementById(containerId.value);
    if (container) lazyLoad?.(container);
  }, 200);
};

onMounted(scanForLazyImages);



return { __sfc: true,props, containerId, itemId, handleUp, handleDown, handleLeftFallback, openItem, instance, lazyLoad, useGetter, currentLanguage, localised, title, subtitle, items, railStyle, loadingIndex, pickToken, cancelPick, pickLuckyDip, leaveUp, leaveDown, focusItem, moveLeft, moveRight, scanForLazyImages, LuckyDipCell, GlyphView }
}

})